import { useEffect } from "react";

/** Elements that rise into view as they enter the viewport. Siblings stagger. */
const REVEAL = [
	".eo-section__overline",
	".eo-section__title",
	".eo-pintro__lead",
	".eo-pintro__actions",
	".eo-pmodes__lead",
	".eo-product",
	".eo-environments__grid img",
	".eo-mission__body > *",
	".eo-mission__logos",
	".eo-feature",
	".eo-rating",
	".eo-pspec__rating",
	".eo-redux__filter",
	".eo-spec",
	".eo-mode",
	".eo-pcall__text",
	".eo-papp__grid img",
	".eo-pcta .eo-button",
	".faq-product",
	".eo-doc",
].join(",");

/** The hero has its own entrance animation. */
const SKIP = ".eo-rhero__text, .eo-rhero__text *, .swiper";

const MAX_STAGGER = 5;
const COUNT_MS = 1400;

function countUp(el: HTMLElement) {
	const node = el.firstChild;
	if (!node || node.nodeType !== Node.TEXT_NODE) return;
	const target = parseFloat(node.textContent || "");
	if (!isFinite(target)) return;
	const start = performance.now();
	const tick = (now: number) => {
		const t = Math.min(1, (now - start) / COUNT_MS);
		const eased = 1 - Math.pow(1 - t, 3);
		node.textContent = String(Math.round(target * eased));
		if (t < 1) requestAnimationFrame(tick);
	};
	node.textContent = "0";
	requestAnimationFrame(tick);
}

/**
 * Scroll-driven motion for the whole site. Tags matching elements with data-reveal and
 * adds `is-in` when they scroll into view; counts the protection ratings up. Does nothing
 * when the visitor prefers reduced motion (the content is then simply shown).
 */
export default function Motion() {
	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const root = document.documentElement;
		let observer: IntersectionObserver | null = null;
		let mutations: MutationObserver | null = null;
		let frame = 0;

		const reveal = (el: Element) => {
			el.classList.add("is-in");
			el.querySelectorAll<HTMLElement>(".eo-rating__value").forEach(countUp);
			if (el.matches(".eo-rating__value")) countUp(el as HTMLElement);
		};

		const scan = () => {
			frame = 0;
			const fresh = Array.from(document.querySelectorAll(REVEAL)).filter(
				(el) => !el.hasAttribute("data-reveal") && !el.matches(SKIP)
			);
			fresh.forEach((el) => {
				const group = el.parentElement ? Array.from(el.parentElement.children).filter((c) => c.matches(REVEAL)) : [el];
				const i = Math.min(group.indexOf(el), MAX_STAGGER);
				(el as HTMLElement).style.setProperty("--reveal-i", String(Math.max(i, 0)));
				el.setAttribute("data-reveal", "");
				observer?.observe(el);
			});
		};
		const schedule = () => {
			if (!frame) frame = requestAnimationFrame(scan);
		};

		const start = () => {
			root.classList.add("has-motion");
			if (!("IntersectionObserver" in window)) {
				root.classList.remove("has-motion");
				return;
			}
			observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((e) => {
						if (!e.isIntersecting) return;
						reveal(e.target);
						observer?.unobserve(e.target);
					});
				},
				{ threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
			);
			mutations = new MutationObserver(schedule);
			mutations.observe(document.getElementById("root") ?? document.body, { childList: true, subtree: true });
			scan();
		};
		const stop = () => {
			root.classList.remove("has-motion");
			observer?.disconnect();
			mutations?.disconnect();
			observer = mutations = null;
		};

		if (!media.matches) start();
		const onChange = () => {
			stop();
			if (!media.matches) start();
		};
		media.addEventListener("change", onChange);
		return () => {
			media.removeEventListener("change", onChange);
			stop();
			if (frame) cancelAnimationFrame(frame);
		};
	}, []);

	return null;
}
